import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { clientsApi } from '@/api/clients-api'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import type { OIDCClient, UpdateClientRequest } from '@/api/types/client'

export default function ClientsPage() {
  const { toast } = useToast()
  const [clientIdInput, setClientIdInput] = useState('')
  const [clientNameInput, setClientNameInput] = useState('')
  const [clientDescriptionInput, setClientDescriptionInput] = useState('')
  const [redirectUriInput, setRedirectUriInput] = useState('')
  const [secretKeyInput, setSecretKeyInput] = useState('')
  
  // Edit client state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [currentClient, setCurrentClient] = useState<OIDCClient | null>(null)
  
  // Fetch clients
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.getClients(),
  })

  const handleCreateClient = async () => {
    try {
      if (!clientIdInput || !clientNameInput || !redirectUriInput || !secretKeyInput) {
        toast({
          title: 'Missing Information',
          description: 'Client ID, Client Name, Redirect URI, and Secret Key are required',
          variant: 'destructive',
        })
        return
      }

      await clientsApi.createClient({
        client_id: clientIdInput,
        client_name: clientNameInput,
        client_description: clientDescriptionInput || undefined,
        redirect_uri: redirectUriInput,
        secret_key: secretKeyInput
      })

      toast({
        title: 'Success',
        description: `Client ${clientIdInput} created successfully`,
      })

      // Reset form and refresh data
      setClientIdInput('')
      setClientNameInput('')
      setClientDescriptionInput('')
      setRedirectUriInput('')
      setSecretKeyInput('')
      refetch()
    } catch (error) {
      console.error('Error creating client:', error)
      toast({
        title: 'Error',
        description: 'Failed to create client. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleEditClient = (client: OIDCClient) => {
    setCurrentClient(client)
    setIsEditDialogOpen(true)
  }

  const handleUpdateClient = async () => {
    if (!currentClient) return

    try {
      const updateData: UpdateClientRequest = {
        client_id: currentClient.client_id,
      }

      // Only include fields that have changed
      if (currentClient.client_name) updateData.client_name = currentClient.client_name
      if (currentClient.client_description !== undefined) updateData.client_description = currentClient.client_description
      if (currentClient.redirect_uri) updateData.redirect_uri = currentClient.redirect_uri
      if (currentClient.secret_key) updateData.secret_key = currentClient.secret_key

      await clientsApi.updateClient(updateData)
      toast({
        title: 'Success',
        description: `Client ${currentClient.client_id} updated successfully`,
      })
      setIsEditDialogOpen(false)
      refetch()
    } catch (error) {
      console.error('Error updating client:', error)
      toast({
        title: 'Error',
        description: 'Failed to update client. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleDeleteClient = async (client_id: string) => {
    try {
      await clientsApi.deleteClient(client_id)
      toast({
        title: 'Success',
        description: `Client ${client_id} deleted successfully`,
      })
      refetch()
    } catch (error) {
      console.error('Error deleting client:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete client. Please try again.',
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">OIDC Clients</h1>
        </div>
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Existing Clients</h2>
          <div className="space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-red-500 p-8" role="alert" aria-live="assertive">
        Error loading clients: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center pb-4">
        <h1 className="text-2xl font-bold">OIDC Clients</h1>
      </div>
      <Separator className="mb-6" />

      {/* Create Client Form */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4" id="create-client-heading">Create New Client</h2>
        <form 
          className="grid gap-4 md:grid-cols-2"
          aria-labelledby="create-client-heading"
          onSubmit={(e) => { e.preventDefault(); handleCreateClient(); }}
        >
          <div className="space-y-2">
            <Label htmlFor="client-id">Client ID</Label>
            <Input
              id="client-id"
              value={clientIdInput}
              onChange={(e) => setClientIdInput(e.target.value)}
              placeholder="Enter client ID"
              aria-required="true"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="client-name">Client Name</Label>
            <Input
              id="client-name"
              value={clientNameInput}
              onChange={(e) => setClientNameInput(e.target.value)}
              placeholder="Enter client name"
              aria-required="true"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="redirect-uri">Redirect URI</Label>
            <Input
              id="redirect-uri"
              value={redirectUriInput}
              onChange={(e) => setRedirectUriInput(e.target.value)}
              placeholder="https://example.com/callback"
              aria-required="true"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="secret-key">Secret Key</Label>
            <Input
              id="secret-key"
              value={secretKeyInput}
              onChange={(e) => setSecretKeyInput(e.target.value)}
              placeholder="Enter client secret key"
              aria-required="true"
              required
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="client-description">Description (Optional)</Label>
            <Input
              id="client-description"
              value={clientDescriptionInput}
              onChange={(e) => setClientDescriptionInput(e.target.value)}
              placeholder="Enter client description"
              aria-required="false"
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" aria-label="Create new OIDC client">
              Create Client
            </Button>
          </div>
        </form>
      </Card>

      {/* Clients List */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Existing Clients</h2>
        {data?.items && data.items.length > 0 ? (
          <ScrollArea className="h-[500px]">
            <Table aria-labelledby="clients-table-caption">
              <caption id="clients-table-caption" className="sr-only">OIDC Clients</caption>
              <TableHeader>
                <TableRow>
                  <TableHead>Client ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Redirect URI</TableHead>
                  <TableHead>Secret Key</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((client: OIDCClient) => (
                  <TableRow key={client.client_id}>
                    <TableCell className="font-medium">
                      <Badge variant="outline" className="font-mono">{client.client_id}</Badge>
                    </TableCell>
                    <TableCell>
                      <div>{client.client_name}</div>
                      {client.client_description && (
                        <p className="text-xs text-muted-foreground mt-1">{client.client_description}</p>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{client.redirect_uri}</TableCell>
                    <TableCell className="font-mono text-xs">{client.secret_key}</TableCell>
                    <TableCell>{new Date(client.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditClient(client)}
                          aria-label={`Edit client ${client.client_id}`}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleDeleteClient(client.client_id)}
                          aria-label={`Delete client ${client.client_id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
            <div className="text-muted-foreground">No clients found.</div>
            <div>Create your first client using the form above.</div>
          </div>
        )}
      </Card>

      {/* Edit Client Dialog */}
      {currentClient && (
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent aria-labelledby="edit-dialog-title" aria-describedby="edit-dialog-description">
            <DialogHeader>
              <DialogTitle id="edit-dialog-title">Edit Client</DialogTitle>
              <DialogDescription id="edit-dialog-description">
                Update the client information. Client ID cannot be changed.
              </DialogDescription>
            </DialogHeader>
            <form 
              className="grid gap-4 py-4" 
              onSubmit={(e) => { e.preventDefault(); handleUpdateClient(); }}
            >
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-client-id" className="text-right">Client ID</Label>
                <Input
                  id="edit-client-id"
                  value={currentClient.client_id}
                  readOnly
                  disabled
                  className="col-span-3"
                  aria-readonly="true"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-client-name" className="text-right">Name</Label>
                <Input
                  id="edit-client-name"
                  value={currentClient.client_name}
                  onChange={(e) => setCurrentClient({...currentClient, client_name: e.target.value})}
                  className="col-span-3"
                  aria-required="true"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-redirect-uri" className="text-right">Redirect URI</Label>
                <Input
                  id="edit-redirect-uri"
                  value={currentClient.redirect_uri}
                  onChange={(e) => setCurrentClient({...currentClient, redirect_uri: e.target.value})}
                  className="col-span-3"
                  aria-required="true"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-secret-key" className="text-right">Secret Key</Label>
                <Input
                  id="edit-secret-key"
                  value={currentClient.secret_key}
                  onChange={(e) => setCurrentClient({...currentClient, secret_key: e.target.value})}
                  className="col-span-3"
                  aria-required="true"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-description" className="text-right">Description</Label>
                <Input
                  id="edit-description"
                  value={currentClient.client_description || ''}
                  onChange={(e) => setCurrentClient({...currentClient, client_description: e.target.value})}
                  placeholder="Optional description"
                  className="col-span-3"
                  aria-required="false"
                />
              </div>
            </form>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} type="button" aria-label="Cancel editing client">Cancel</Button>
              <Button onClick={handleUpdateClient} type="submit" aria-label={`Save changes to client ${currentClient.client_id}`}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}